import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  gaEventRoomJoin,
  type RoomDiscoveryChannel,
} from "../../../tools/analytics/domains/invite.analytics";
import { displayToast } from "../../../components/app/AppToast";
import {
  apiClient,
  useApiQuery,
  useApiRevalidate,
} from "../../../services/api";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../user/hooks/useCurrentUser";
import { openRoomWelcome } from "../../../components/room/RoomWelcomeHost";
import {
  askForProfessionalRecord,
  needsProfessionalRecord,
} from "../../../components/room/ProfessionalRecordHost";

/**
 * The single way to join a room from inside the app.
 *
 * Joining is never just a POST: a room that asks for a professional record
 * asks for it *before* membership is granted, the welcome sheet opens after it
 * (docs/partner-objection.md, REQ-05), and every join feeds `room_joined`
 * analytics. Keeping all of it here is what stops call sites from re-deriving
 * a partial version.
 */
export function useJoinRoom(
  roomId: string | null,
  roomDiscoveryChannel: RoomDiscoveryChannel,
  // `greet`: the welcome sheet. `silent`: nothing, for a join that puts back a
  // membership the reader just left (Undo), who already went through the
  // room's welcome.
  afterJoin: "greet" | "silent"
): { joinRoom: () => Promise<void>; isJoining: boolean } {
  const { t } = useTranslation();
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const { currentUser } = useCurrentUser();
  const revalidate = useApiRevalidate();
  const [isJoining, setJoining] = useState(false);

  async function joinRoom(): Promise<void> {
    if (currentRoom === null || roomId === null || isJoining) return;

    // A room that asks for a professional record admits nobody without one:
    // the reader is asked first, and a cancel is no join.
    if (
      currentUser !== null &&
      needsProfessionalRecord(currentRoom, currentUser)
    ) {
      const outcome = await askForProfessionalRecord();
      if (outcome === "cancelled") return;
    }

    setJoining(true);
    try {
      await apiClient.post("/webapp/private/rooms/:id/join", {
        param: { id: roomId },
        json: { discoveryChannel: roomDiscoveryChannel },
      });
      gaEventRoomJoin({
        roomUid: roomId,
        userUid: currentUser === null ? undefined : currentUser.uid,
        roomName: currentRoom.translations.en.name,
        roomType: currentRoom.isPublic ? "public" : "private",
        roomCount: currentUser === null ? undefined : currentUser.rooms.length,
        roomDiscoveryChannel,
      });
      await revalidate("/webapp/private/rooms");
      void revalidate("/webapp/private/playlists/recommended");
      void reloadCurrentUser();
      await revalidate("/webapp/private/rooms/:id", { param: { id: roomId } });
      if (afterJoin === "silent") return;
      openRoomWelcome(roomId, roomDiscoveryChannel);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setJoining(false);
    }
  }

  return { joinRoom, isJoining };
}
